import { useParams, Link } from 'react-router-dom';
import { articles, categories } from '@/mocks/data';
import PageSEO from '@/components/feature/PageSEO';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

function renderContent(content: string) {
  const lines = content.split('\n');
  const elements: JSX.Element[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('## ')) {
      elements.push(
        <h2 key={i} className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mt-10 mb-4">
          {line.replace('## ', '')}
        </h2>
      );
      i++;
      continue;
    }

    if (line.startsWith('**') && line.endsWith('**')) {
      elements.push(
        <h3 key={i} className="font-heading text-base font-semibold text-foreground-800 mt-6 mb-3">
          {line.replace(/\*\*/g, '')}
        </h3>
      );
      i++;
      continue;
    }

    if (line.startsWith('- ')) {
      const listItems: string[] = [];
      while (i < lines.length && lines[i].startsWith('- ')) {
        listItems.push(lines[i].replace('- ', ''));
        i++;
      }
      elements.push(
        <ul key={i} className="list-disc pl-5 space-y-1.5 mb-4 text-foreground-700 text-sm md:text-base">
          {listItems.map((item, idx) => (
            <li key={idx}>{item.replace(/\*\*/g, '')}</li>
          ))}
        </ul>
      );
      continue;
    }

    if (line.trim() === '') {
      i++;
      continue;
    }

    elements.push(
      <p key={i} className="text-foreground-700 text-sm md:text-base leading-relaxed mb-4">
        {line.replace(/\*\*/g, '')}
      </p>
    );
    i++;
  }

  return elements;
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);
  const category = article ? categories.find((c) => c.slug === article.category) : null;
  const relatedArticles = article
    ? articles.filter((a) => a.category === article.category && a.id !== article.id).slice(0, 3)
    : [];

  if (!article) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-3">文章未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm whitespace-nowrap cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.coverImage,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.publishDate,
    publisher: {
      '@type': 'Organization',
      name: '年轻的朋友韩国',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://shopkorea.github.io/article/${article.slug}`,
    },
  };

  return (
    <>
      <PageSEO
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`/article/${article.slug}`}
        ogType="article"
        ogImage={article.coverImage}
        schema={articleSchema}
      />
      <ScrollToTop />
      <Navbar />
      <main>
        <article className="pt-24 md:pt-28 pb-14 md:pb-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                ...(category ? [{ label: category.name, href: `/category/${category.slug}` }] : []),
                { label: article.title },
              ]}
            />

            <header className="mt-6 mb-8">
              <div className="flex flex-wrap gap-2 mb-4">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
              <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full overflow-hidden bg-background-200">
                    <img
                      src={article.authorAvatar}
                      alt={article.author}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <span className="font-medium text-foreground-700">{article.author}</span>
                </div>
                <span className="text-foreground-300">·</span>
                <span>{article.publishDate}</span>
                <span className="text-foreground-300">·</span>
                <span>阅读约{article.readTime}分钟</span>
              </div>
            </header>

            <div className="relative h-[300px] md:h-[420px] rounded-xl overflow-hidden mb-10">
              <img
                src={article.coverImage}
                alt={article.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            <div className="prose-custom">
              {renderContent(article.content || article.excerpt)}
            </div>

            {!article.content && (
              <div className="text-center py-16 text-foreground-400">
                <p>完整内容正在整理中，敬请期待~</p>
              </div>
            )}

            <div className="mt-12 pt-8 border-t border-background-200/60">
              <div className="flex flex-wrap gap-2">
                <span className="text-sm text-foreground-500">标签：</span>
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-background-100 text-foreground-600 text-xs rounded-full whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 px-4 md:px-6 bg-background-100">
            <div className="max-w-7xl mx-auto">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <Link
                    key={ra.id}
                    to={`/article/${ra.slug}`}
                    className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/60 hover:border-primary-300 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative h-40 md:h-44 overflow-hidden">
                      <img
                        src={ra.coverImage}
                        alt={ra.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 mb-2">
                        {ra.title}
                      </h3>
                      <p className="text-xs text-foreground-500 line-clamp-2">{ra.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}